import { useSyncExternalStore } from 'react';

/**
 * Shell breakpoints, worked out from the real column widths (ADR 0008). The conversation always keeps
 * its 480px minimum; when the window is narrower, things give way in this order: the details panel,
 * then the full sidebar, then the side-by-side inbox list.
 *
 *   navigation 232 + list 360 + conversation 480 + details 320 = 1392  details panel as a column
 *   navigation 232 + list 360 + conversation 480               = 1072  full sidebar
 *   rail        72 + list 360 + conversation 480               =  912  list beside the conversation
 *   below 912: rail + the list or the conversation, one at a time (back arrow)
 *   below 768: the phone layout (top bar, tab bar)
 *
 * The same numbers are Tailwind breakpoints in src/index.css (`split:`, `nav:`, `wide:`); a unit test
 * keeps the two in sync.
 */
export const SHELL_WIDTHS = {
  nav: 232,
  rail: 72,
  list: 360,
  conversationMin: 480,
  details: 320,
} as const;

export const BREAKPOINTS = {
  desktop: 768,
  split: SHELL_WIDTHS.rail + SHELL_WIDTHS.list + SHELL_WIDTHS.conversationMin,
  nav: SHELL_WIDTHS.nav + SHELL_WIDTHS.list + SHELL_WIDTHS.conversationMin,
  wide: SHELL_WIDTHS.nav + SHELL_WIDTHS.list + SHELL_WIDTHS.conversationMin + SHELL_WIDTHS.details,
} as const;

/** In rem, like the CSS breakpoints, so code and CSS switch at the same point at any font size. */
const minWidth = (px: number) => `(min-width: ${px / 16}rem)`;
export const DESKTOP_QUERY = minWidth(BREAKPOINTS.desktop);
export const SPLIT_QUERY = minWidth(BREAKPOINTS.split);
export const NAV_QUERY = minWidth(BREAKPOINTS.nav);
export const WIDE_QUERY = minWidth(BREAKPOINTS.wide);
/** Phones and tablets: Enter adds a line, the send button sends. */
export const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
}
